<template>
  <GeneralDialog
    v-model:visible="visible"
    @open="onModalOpen"
    @dismiss="onModalDismiss"
    :fullscreen="true"
  >
    <div
      v-if="!releaseItemLoading && !collectionItemsLoading"
      class="h-full bg-main-background p-3.75 pb-0 flex flex-col"
      :style="{ paddingTop: `calc(${insets.top}px + 15px)` }"
      ref="mainScrollContainer"
    >
      <div class="flex flex-col mb-7.5 w-full gap-1.25">
        <span :class="$typo.openSansBold20" class="text-text flex items-center gap-1 leading-6.25!">
          <IconCustom
            data-test-id="bulk-edit-back"
            ligature="arrow_left"
            size="25"
            @click="emit('dismiss')"
          />{{ t(header) }}</span
        >
        <span :class="$typo.openSans12" class="flex-1 text-text-muted pl-7.5">{{ entryName }}</span>
      </div>
      <div class="flex gap-3.75 mb-2.5">
        <div class="flex-1 flex flex-col gap-3.75">
          <span :class="$typo.openSans12" class="text-text-muted">{{ t('collectionStatus') }}</span>
          <ShortDropdown
            data-test-id="bulk-edit-collect-status"
            v-model="collectStatus"
            :options="collectStatusOptions"
          />
        </div>
        <div class="flex-1 flex flex-col gap-3.75">
          <span :class="$typo.openSans12" class="text-text-muted">{{ t('readingStatus') }}</span>
          <ShortDropdown
            data-test-id="bulk-edit-read-status"
            v-model="currentReadStatus"
            :options="readStatusOptions"
          />
        </div>
      </div>
      <div class="flex-1 min-h-0 bg-secondary-background -mx-3.75">
        <DynamicTabView
          v-if="bulkEditTabConfigs.length > 1"
          :start-tab-configs="bulkEditTabConfigs"
          :tab-width-map="bulkEditTabWidthMap"
          :start-key="initialCollectionTypeTab"
          :static-tabs="true"
          :line-padding="0"
          :page-height-scroll="pageHeightScroll"
          :select-tab="(key) => (selectedCollectionTypeTab = key as CollectionBulkEditTab)"
        >
          <template #tabContent="{ tabConfig }">
            <VolumeSelectionTable
              v-if="tabConfig.key === 'volume'"
              :class="['w-full px-3.75']"
              :sorted-volume-numbers="sortedVolumeNumbers"
              :sorted-volume-numbers-without-suffix="sortedVolumeNumbersWithoutSuffix"
              :not-released-volume-numbers="notReleasedVolumeNumbers"
              :not-released-volume-numbers-without-suffix="notYetReleasedVolumeNumbersWithoutSuffix"
              :owned-map="ownedMap"
              :read-map="readMap"
              @update:owned-map="onOwnedMapEdited"
              @update:read-map="onReadMapEdited"
              @edit-owned-volume-entry="(volume) => onOwnedVolumeEdit(volume)"
              :preselected-versions="internalPreselectedVersions"
              :preselected-read-numbers="internalPreselectedReadNumbers"
              :preselected-icon="preselectedIcon"
            />
            <CollectionSelectionTable
              v-if="tabConfig.key === 'collection'"
              :class="['w-full px-3.75']"
              v-model:misc-owned-map="miscOwnedMap"
              @miscOwnedMapUpdate="onUserCollectionChange"
              :collection-entries="fetchedReleaseItemCollections"
              :entry-id="entryId"
              :preselected-collection-id="selectedCollectionId"
              :preselected-icon="preselectedIcon"
            />
          </template>
        </DynamicTabView>
        <VolumeSelectionTable
          v-else
          :class="['w-full px-3.75']"
          :sorted-volume-numbers="sortedVolumeNumbers"
          :sorted-volume-numbers-without-suffix="sortedVolumeNumbersWithoutSuffix"
          :not-released-volume-numbers="notReleasedVolumeNumbers"
          :not-released-volume-numbers-without-suffix="notYetReleasedVolumeNumbersWithoutSuffix"
          :owned-map="ownedMap"
          :read-map="readMap"
          @update:owned-map="onOwnedMapEdited"
          @update:read-map="onReadMapEdited"
          @edit-owned-volume-entry="(volume) => onOwnedVolumeEdit(volume)"
          :preselected-versions="internalPreselectedVersions"
          :preselected-read-numbers="internalPreselectedReadNumbers"
          :preselected-icon="preselectedIcon"
        />
      </div>
      <div
        class="flex justify-between -mx-3.75 px-3.75 pt-5 bg-secondary-background relative"
        :style="{ paddingBottom: `calc(${insets.bottom}px + 30px)` }"
      >
        <div class="flex gap-2.5 justify-start">
          <Button
            data-test-id="bulk-edit-delete"
            :destructive="true"
            v-if="collectionEntry && !props.localOnly"
            @click="() => (deleteVisible = true)"
            :class="$typo.openSans12"
          >
            <IconCustom ligature="delete" size="16" />
            {{ t('deleteCollectionButton') }}
          </Button>
        </div>
        <div class="flex gap-2.5 justify-end">
          <Button data-test-id="bulk-edit-save" @click="saveCollection" :class="$typo.openSans12">
            <IconCustom ligature="save" size="16" />
            {{ t('collectionModalBulkEditUpdate') }}
          </Button>
        </div>
      </div>
    </div>
  </GeneralDialog>

  <GeneralDialog v-model:visible="deleteVisible">
    <div class="p-3.75">
      <div class="flex-row items-center mb-7.5">
        <h3
          :class="[$typo.openSansBold16]"
          class="text-text mb-7.5 flex justify-between items-center"
        >
          {{ t('confirmReleaseDeleteHeader') }}
          <IconCustom class="text-text-muted" ligature="delete" :size="'16'"></IconCustom>
        </h3>
        <p :class="[$typo.openSans12]" class="text-text-muted">
          {{ t('collectionModalDeleteWarningText', { title: entryName }) }}
        </p>
      </div>
      <div class="flex justify-between">
        <Button
          data-test-id="bulk-edit-delete-confirm"
          :destructive="true"
          @click="onDeleteAccept"
          :class="$typo.openSans12"
        >
          <IconCustom ligature="delete" size="16"></IconCustom>
          {{ t('collectionModalBulkEditAcceptDelete') }}
        </Button>
        <Button
          data-test-id="bulk-edit-delete-cancel"
          :class="$typo.openSans12"
          @click="() => (deleteVisible = false)"
        >
          <IconCustom ligature="close" size="16"></IconCustom>
          {{ t('collectionModalBulkEditCancel') }}
        </Button>
      </div>
    </div>
  </GeneralDialog>

  <OwnedVolumeEditModal
    :volume-number="selectedOwnedVolumeForEdit"
    :entry-name="entryName"
    v-model:visible="ownedVolumeEditModalVisible"
    :owned-map="ownedMap"
    @update:owned-map="onOwnedMapEdited"
    :sorted-volume-numbers="sortedVolumeNumbers"
  ></OwnedVolumeEditModal>
</template>

<script lang="ts" setup>
import { computed, provide, Ref, ref, toRefs } from 'vue'
import { catalogQuery } from '@/data/catalogClient.ts'
import { isConnectivityError } from '@/vendor/apollo-client.ts'
import VolumeSelectionTable from '@/components/VolumeSelectionTable.vue'
import IconCustom from '@/components/IconCustom.vue'
import {
  COLLECT_STATUS,
  COLLECTION_STATUS_LABEL_MAP,
  getMonth,
  getYear,
  OwnedSelectionMap,
  READ_STATUS_LABEL_MAP,
  ReadSelectionMap,
  CollectionBulkEditTab,
  BulkEditModalProps,
  getVersionFromVolume,
  TabConfig,
  formatVolumeRange,
} from '@/utils'
import { showToast } from '@/utils/showToast'
import { sendError } from '@/utils/sendError'
import { useLocalizationStore } from '@/stores/localization.ts'

import {
  getNewOwnedCollectionStatus,
  getNewReadCollectionStatus,
  getVolumeNumbersFromVersions,
  isEntryStatusComplete,
  MiscOwnedMap,
  UserCollection,
  useUserCollectionStore,
} from '@/stores/user-collection'
import GeneralDialog from '@/components/GeneralDialog.vue'
import $typo from '@/styles/typografy.module.css'

import { GET_RELEASE_ENTRY_WITH_SERIES_INFO, GET_RELEASE_ITEM_COLLECTIONS } from '@/queries'
import {
  OrderDirection,
  ReleaseEntry,
  ReleaseItemCollection,
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
} from '../../__generated__/graphql.ts'
import OwnedVolumeEditModal from '@/components/OwnedVolumeEditModal.vue'
import CollectionSelectionTable from '@/components/CollectionSelectionTable.vue'
import DynamicTabView from '@/components/DynamicTabView.vue'
import ShortDropdown from '@/components/ShortDropdown.vue'
import { useInfoStore } from '@/stores/info.ts'
import Button from '@/components/Button.vue'

const props = defineProps<BulkEditModalProps>()

const { insets } = useInfoStore()
const pageHeightScroll = ref({
  currentPageScroll: 0,
  startTabScrollPosition: 0,
})

const scrollContainer = ref<HTMLElement | null>(null)

provide<Ref<HTMLElement>>('mainScrollContainer', scrollContainer)

const releaseItemLoading = ref(false)
const collectionItemsLoading = ref(false)
const fetchedReleaseEntry = ref<ReleaseEntry | null>(null)
const fetchedReleaseItemCollections = ref<ReleaseItemCollection[]>([])

const {
  entryId,
  collectionEntry,
  preselectedVersions,
  preselectedReadNumbers,
  preselectedIcon,
  header,
  selectedCollectionId,
  defaultSelectedTab,
  preselectedCollectionStatus,
} = toRefs(props)

const selectedCollectionTypeTab = ref<CollectionBulkEditTab>(defaultSelectedTab.value || 'volume')
const initialCollectionTypeTab = ref<CollectionBulkEditTab>(defaultSelectedTab.value || 'volume')
const visible = defineModel('visible', { default: false })

const bulkEditTabConfigs = computed<TabConfig<{ id: string }>[]>(() => {
  const configs: TabConfig<{ id: string }>[] = [
    { key: 'volume', title: t('releaseTabVolumes'), tabData: { id: 'volume' }, widthKey: 'tab' },
  ]
  if (fetchedReleaseItemCollections.value.length > 0) {
    configs.push({
      key: 'collection',
      title: t('releaseTabCollections'),
      tabData: { id: 'collection' },
      widthKey: 'tab',
    })
  }
  return configs
})
const bulkEditTabWidthMap: Record<string, number> = { tab: 120 }

const selectedOwnedVolumeForEdit = ref('')
const ownedVolumeEditModalVisible = ref(false)

function reportLoadError(error: Error): void {
  sendError(error, { source: 'CollectionBulkEditModal' })
  void showToast({ text: t('bulkEditUnknownError'), duration: 'long' })
}

const isSupersededRequest = (requestedEntryId: string) => requestedEntryId !== entryId.value

async function loadReleaseEntry(requestedEntryId: string) {
  releaseItemLoading.value = true
  try {
    const result = await catalogQuery({
      query: GET_RELEASE_ENTRY_WITH_SERIES_INFO,
      variables: { where: { id: requestedEntryId } },
    })
    if (isSupersededRequest(requestedEntryId)) {
      return
    }
    fetchedReleaseEntry.value = result.data?.releaseEntry || null
    entryName.value = fetchedReleaseEntry.value?.internalName || ''
  } catch (e) {
    if (isConnectivityError(e)) {
      // known: handled by the global offline toast, nothing to surface here.
    } else if (e instanceof Error) {
      reportLoadError(e)
    } else {
      throw e
    }
  }
  releaseItemLoading.value = false
}

async function loadReleaseCollections(requestedEntryId: string) {
  collectionItemsLoading.value = true
  try {
    const result = await catalogQuery({
      query: GET_RELEASE_ITEM_COLLECTIONS,
      variables: {
        where: { entryId: { equals: requestedEntryId } },
        orderBy: { releaseDate: OrderDirection.Asc },
      },
    })
    if (isSupersededRequest(requestedEntryId)) {
      return
    }
    fetchedReleaseItemCollections.value = result.data?.releaseItemCollections || []
  } catch (e) {
    if (isConnectivityError(e)) {
      // known: handled by the global offline toast, nothing to surface here.
    } else if (e instanceof Error) {
      reportLoadError(e)
    } else {
      throw e
    }
  }
  collectionItemsLoading.value = false
}

const emit = defineEmits<{
  (e: 'collectionSaved'): void
  (e: 'collectionDeleted'): void
  (e: 'dismiss'): void
  (
    e: 'localSave',
    data: {
      ownedMap: OwnedSelectionMap
      miscOwnedMap: MiscOwnedMap
      readMap: ReadSelectionMap
      collectionStatus: UserReleaseEntryStatusCollectionStatusType
      currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType
    },
  ): void
}>()

const { t } = useLocalizationStore()

const isNewCollection = computed(() => !collectionEntry.value)
const deleteVisible = ref(false)
const { createCollectionEntry, updateCollectionData, updateLatestReadMap, removeCollectionEntry } =
  useUserCollectionStore()

const entryName = ref(fetchedReleaseEntry.value?.internalName || '')

const sortedVolumeNumbers = computed<string[]>(
  () => fetchedReleaseEntry.value?.sortedVolumeNumbers || [],
)
const notReleasedVolumeNumbers = computed<string[]>(
  () => fetchedReleaseEntry.value?.notReleasedVolumeNumbers || [],
)

const onOwnedVolumeEdit = (volume: string) => {
  selectedOwnedVolumeForEdit.value = volume
  ownedVolumeEditModalVisible.value = true
}

const sortedVolumeNumbersWithoutSuffix = computed(() =>
  getVolumeNumbersFromVersions(sortedVolumeNumbers.value),
)
const notYetReleasedVolumeNumbersWithoutSuffix = computed(() =>
  getVolumeNumbersFromVersions(notReleasedVolumeNumbers.value),
)

const readMap = ref<ReadSelectionMap>({})
const ownedMap = ref<OwnedSelectionMap>({})
const miscOwnedMap = ref<MiscOwnedMap>({})
const collectStatus = ref<UserReleaseEntryStatusCollectionStatusType>(
  UserReleaseEntryStatusCollectionStatusType.Collecting,
)
const currentReadStatus = ref<UserReleaseEntryStatusCurrentReadStatusType>(
  UserReleaseEntryStatusCurrentReadStatusType.Planned,
)

const onUserCollectionChange = async (id: string, change: MiscOwnedMap[string]) => {
  if (change.quantity === 0) {
    return
  }
  const itemCollection = fetchedReleaseItemCollections.value.find((item) => item.id === id)
  if (!itemCollection) {
    return
  }
  const releaseItems = itemCollection.releaseItems || []
  const newItems = releaseItems.filter(
    (releaseItem) => !ownedMap.value[getVersionFromVolume(releaseItem)],
  )

  const currentEntryNumbers: string[] = []
  const otherEntries = new Map<string, { title: string; numbers: string[] }>()
  for (const releaseItem of newItems) {
    if (!releaseItem.number) continue
    if (releaseItem.entryId === entryId.value) {
      currentEntryNumbers.push(releaseItem.number)
    } else if (releaseItem.entryId) {
      const group = otherEntries.get(releaseItem.entryId) ?? {
        title: releaseItem.localizedSeriesTitle ?? '',
        numbers: [],
      }
      group.numbers.push(releaseItem.number)
      otherEntries.set(releaseItem.entryId, group)
    }
  }

  if (currentEntryNumbers.length > 0) {
    showToast({
      text: t('addedVolumesToCollection', {
        range: formatVolumeRange(currentEntryNumbers),
        count: currentEntryNumbers.length,
      }),
    })
  }
  for (const group of otherEntries.values()) {
    showToast({
      text: t('addedTitledVolumesToCollection', {
        title: group.title,
        range: formatVolumeRange(group.numbers),
        count: group.numbers.length,
      }),
    })
  }

  const newOwnedMap = { ...ownedMap.value }
  releaseItems.forEach((releaseItem) => {
    newOwnedMap[getVersionFromVolume(releaseItem)] = 1
  })
  onOwnedMapEdited(newOwnedMap)
}

const completeOwnedMap = computed<OwnedSelectionMap>(() => {
  const map: OwnedSelectionMap = {}
  for (const volumeNumber of sortedVolumeNumbers.value) {
    map[volumeNumber] = ownedMap.value[volumeNumber] || 0
  }
  return map
})
const completedReadMap = computed<ReadSelectionMap>(() => {
  const map: ReadSelectionMap = {}
  for (const volumeNumber of sortedVolumeNumbers.value) {
    map[volumeNumber] = readMap.value[volumeNumber] || false
  }
  return map
})

const internalPreselectedVersions = ref<string[] | undefined>()
const internalPreselectedReadNumbers = ref<string[] | undefined>()

const collectStatusOptions = computed(() =>
  COLLECT_STATUS.map((status) => ({
    label: t(COLLECTION_STATUS_LABEL_MAP[status]),
    value: status,
  })),
)
const readStatusOptions = computed(() =>
  Object.values(UserReleaseEntryStatusCurrentReadStatusType).map((status) => ({
    label: t(READ_STATUS_LABEL_MAP[status]),
    value: status,
  })),
)
const statusDerivationEntry = computed(() => ({
  entryCompleted: isEntryStatusComplete(fetchedReleaseEntry.value?.status),
  sortedVolumeVersions: sortedVolumeNumbers.value,
}))

const onOwnedMapEdited = (editedOwnedMap: OwnedSelectionMap) => {
  collectStatus.value = getNewOwnedCollectionStatus(ownedMap.value, editedOwnedMap, {
    ...statusDerivationEntry.value,
    collectionStatus: collectStatus.value,
  })
  ownedMap.value = editedOwnedMap
}

const onReadMapEdited = (editedReadMap: ReadSelectionMap) => {
  currentReadStatus.value = getNewReadCollectionStatus(readMap.value, editedReadMap, {
    ...statusDerivationEntry.value,
    currentReadStatus: currentReadStatus.value,
  })
  readMap.value = editedReadMap
}

const hydrateMapsFromCollectionEntry = () => {
  readMap.value = collectionEntry.value ? { ...collectionEntry.value.latestReadMap } : {}
  ownedMap.value = collectionEntry.value ? { ...collectionEntry.value.ownedMap } : {}
  miscOwnedMap.value = collectionEntry.value ? { ...collectionEntry.value.miscOwnedMap } : {}
}
const changes = computed(() => {
  const changes: Partial<UserCollection> = {}
  if (collectionEntry.value) {
    const entry = collectionEntry.value
    const changeInOwnedStatus = sortedVolumeNumbers.value.some(
      (key) => entry.ownedMap?.[key] !== ownedMap.value?.[key],
    )
    if (changeInOwnedStatus) {
      changes.ownedMap = completeOwnedMap.value
    }

    const changeInMiscOwnedStatus = Object.keys(miscOwnedMap.value).some(
      (key) => entry.miscOwnedMap?.[key]?.quantity !== miscOwnedMap.value?.[key]?.quantity,
    )
    if (changeInMiscOwnedStatus) {
      changes.miscOwnedMap = miscOwnedMap.value
    }
    const changeInReadStatus = sortedVolumeNumbers.value.some(
      (key) => entry.latestReadMap?.[key] !== readMap.value?.[key],
    )
    if (changeInReadStatus) {
      changes.latestReadMap = completedReadMap.value
    }
    if (entry.collectionStatus !== collectStatus.value) {
      changes.collectionStatus = collectStatus.value
    }
    if (entry.currentReadStatus !== currentReadStatus.value) {
      changes.currentReadStatus = currentReadStatus.value
    }
  } else {
    if (Object.keys(ownedMap.value).length > 0) {
      changes.ownedMap = completeOwnedMap.value
    }
    if (Object.keys(readMap.value).length > 0) {
      changes.latestReadMap = completedReadMap.value
    }
    if (collectStatus.value !== 'collecting') {
      changes.collectionStatus = collectStatus.value
    }
    if (currentReadStatus.value !== 'planned') {
      changes.currentReadStatus = currentReadStatus.value
    }
    if (Object.keys(miscOwnedMap.value).length > 0) {
      changes.miscOwnedMap = miscOwnedMap.value
    }
  }
  return changes
})

function onModalDismiss() {
  emit('dismiss')
}

function onModalOpen() {
  fetchedReleaseEntry.value = null
  fetchedReleaseItemCollections.value = []
  loadReleaseEntry(entryId.value)
  loadReleaseCollections(entryId.value)
  selectedCollectionTypeTab.value = defaultSelectedTab.value
  initialCollectionTypeTab.value = defaultSelectedTab.value
  internalPreselectedVersions.value = preselectedVersions.value
  internalPreselectedReadNumbers.value = preselectedReadNumbers.value
  hydrateMapsFromCollectionEntry()
  if (collectionEntry.value) {
    collectStatus.value = collectionEntry.value.collectionStatus
    currentReadStatus.value = collectionEntry.value.currentReadStatus
  } else {
    collectStatus.value =
      preselectedCollectionStatus.value ?? UserReleaseEntryStatusCollectionStatusType.Planned
    currentReadStatus.value = UserReleaseEntryStatusCurrentReadStatusType.Planned
  }
}

async function saveCollection() {
  if (props.localOnly) {
    emit('localSave', {
      ownedMap: ownedMap.value,
      miscOwnedMap: miscOwnedMap.value,
      readMap: readMap.value,
      collectionStatus: collectStatus.value,
      currentReadStatus: currentReadStatus.value,
    })
    visible.value = false
    return
  }
  if (isNewCollection.value) {
    createCollectionEntry({
      readId: null,
      collectionId: null,
      entryId: entryId.value,
      startYear: getYear(fetchedReleaseEntry.value?.startDate),
      startMonth: getMonth(fetchedReleaseEntry.value?.startDate),
      endYear: getYear(fetchedReleaseEntry.value?.endDate),
      endMonth: getMonth(fetchedReleaseEntry.value?.endDate),
      collectionStatus: collectStatus.value,
      currentReadStatus: currentReadStatus.value,
      latestReadMap: readMap.value,
      entryCompleted: isEntryStatusComplete(fetchedReleaseEntry.value?.status),
      sortedVolumeNumbers: sortedVolumeNumbers.value,
      notReleasedVolumeNumbers: notReleasedVolumeNumbers.value,
      ownedMap: completeOwnedMap.value,
      miscOwnedMap: changes.value.miscOwnedMap,
    })
  } else if (collectionEntry.value) {
    if (Object.keys(changes.value).length > 0) {
      updateCollectionData(entryId.value, {
        ownedMap: changes.value.ownedMap,
        collectionStatus: changes.value.collectionStatus,
        currentReadStatus: changes.value.currentReadStatus,
        miscOwnedMap: changes.value.miscOwnedMap,
      })
    }
    if (changes.value.latestReadMap) {
      updateLatestReadMap(entryId.value, changes.value.latestReadMap)
    }
  }

  emit('collectionSaved')
}

const onDeleteAccept = () => {
  if (entryId.value) {
    removeCollectionEntry(entryId.value)
    deleteVisible.value = false
    emit('collectionDeleted')
  }
}
</script>
